/**
 * Team Owner Dashboard
 * View for team owners to monitor their team during auction
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { formatBYC, PLAYER_STATUS_COLORS } from './useAuction';

export default function TeamOwnerDashboard({ eventId, config, userId }) {
  const [myTeam, setMyTeam] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [squad, setSquad] = useState([]);
  const [maxBid, setMaxBid] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Get all teams
      const teamsRes = await axiosInstance.get(`/v1/events/${eventId}/auction/teams`);
      setAllTeams(teamsRes.data);
      
      // Find my team
      const team = teamsRes.data.find(t => 
        t.owners?.some(o => o.user_id === userId)
      );
      
      if (team) {
        setMyTeam(team);
        
        // Fetch team-specific data
        const [walletRes, squadRes, maxBidRes] = await Promise.all([
          axiosInstance.get(`/v1/events/${eventId}/auction/teams/${team.id}/wallet`),
          axiosInstance.get(`/v1/events/${eventId}/auction/teams/${team.id}/squad`),
          axiosInstance.get(`/v1/events/${eventId}/auction/teams/${team.id}/max-bid`).catch(() => ({ data: null }))
        ]);
        
        setWallet(walletRes.data);
        setSquad(squadRes.data);
        setMaxBid(maxBidRes.data);
      }
      
      // Get current player if auction is live
      if (config?.auction_status === 'LIVE') {
        try {
          const playerRes = await axiosInstance.get(`/v1/events/${eventId}/auction/current-player`);
          setCurrentPlayer(playerRes.data);
        } catch {
          setCurrentPlayer(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId, config?.auction_status]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  if (!myTeam) {
    return (
      <div className="bg-white rounded-xl p-8 border border-gray-100 text-center" data-testid="no-team-message">
        <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-warning text-2xl text-amber-600"></i>
        </div>
        <h3 className="font-bold text-lg mb-2">No Team Assigned</h3>
        <p className="text-gray-500">You haven't been assigned to any team for this auction yet.</p>
        <p className="text-sm text-gray-400 mt-2">Please contact the tournament admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-owner-dashboard">
      {/* My Team Card */}
      <div className="bg-gradient-to-br from-[#2B3A67] to-[#1e2a4a] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
            {myTeam.team_logo_url ? (
              <img src={myTeam.team_logo_url} alt={myTeam.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-extrabold">{myTeam.team_name[0]}</span>
            )}
          </div>
          <div>
            <h2 className="text-2xl font-extrabold">{myTeam.team_name}</h2>
            <p className="text-white/70 text-sm">Your Team Dashboard</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-3xl font-extrabold text-[#F4D341]">₿ {formatBYC(wallet?.balance_byc || 0)}</p>
            <p className="text-xs text-white/60 mt-1">Wallet Balance</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-3xl font-extrabold">{squad.length}</p>
            <p className="text-xs text-white/60 mt-1">Squad Size</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-3xl font-extrabold">{myTeam.remaining_slots}</p>
            <p className="text-xs text-white/60 mt-1">Slots Left</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <p className="text-3xl font-extrabold text-green-400">₿ {formatBYC(maxBid?.max_bid_byc || 0)}</p>
            <p className="text-xs text-white/60 mt-1">Max Bid Allowed</p>
          </div>
        </div>
      </div>

      {/* Current Player (Live View) */}
      {config?.auction_status === 'LIVE' && currentPlayer && (
        <div className="bg-white rounded-xl border-2 border-green-500 p-5" data-testid="current-player-live">
          <div className="flex items-center justify-between mb-4">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              LIVE NOW
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#2B3A67] flex items-center justify-center">
              <span className="text-white text-xl font-bold">{currentPlayer.player_name?.[0]}</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">{currentPlayer.player_name}</h3>
              <p className="text-sm text-gray-500">{currentPlayer.player_email}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Base Price</p>
              <p className="text-xl font-bold">₿ {formatBYC(currentPlayer.base_price_byc)}</p>
            </div>
          </div>
        </div>
      )}

      {/* All Teams Wallet Transparency */}
      <div className="bg-white rounded-xl p-5 border border-gray-100">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-chart-bar text-[#3AAFA9]"></i>
          All Teams Status
        </h3>
        <div className="space-y-3">
          {allTeams.sort((a, b) => (b.wallet_balance_byc || 0) - (a.wallet_balance_byc || 0)).map((team, idx) => (
            <div
              key={team.id}
              className={`flex items-center justify-between p-3 rounded-xl ${
                team.id === myTeam.id ? 'bg-[#2B3A67]/10 border border-[#2B3A67]/20' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
                    {team.team_logo_url ? (
                      <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-sm font-bold">{team.team_name[0]}</span>
                    )}
                  </div>
                  <span className="font-medium">
                    {team.team_name}
                    {team.id === myTeam.id && (
                      <span className="ml-2 text-xs bg-[#3AAFA9] text-white px-2 py-0.5 rounded">YOU</span>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="font-bold">₿ {formatBYC(team.wallet_balance_byc || 0)}</p>
                  <p className="text-xs text-gray-400">Balance</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{team.squad_count || 0}</p>
                  <p className="text-xs text-gray-400">Players</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* My Squad */}
      <div className="bg-white rounded-xl p-5 border border-gray-100">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-users text-[#E54562]"></i>
          My Squad ({squad.length})
        </h3>
        {squad.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No players acquired yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {squad.map(player => (
              <div key={player.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="relative">
                  {player.player_photo_url ? (
                    <img 
                      src={player.player_photo_url} 
                      alt={player.player_name} 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#2B3A67] flex items-center justify-center">
                      <span className="text-white font-bold">{player.player_name?.[0]}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{player.player_name}</p>
                  {/* Skills Capsules */}
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      player.acquisition_type === 'ICON' ? 'bg-purple-100 text-purple-600' :
                      player.acquisition_type === 'OWNER_PLAYER' ? 'bg-amber-100 text-amber-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {player.acquisition_type}
                    </span>
                    {player.player_category && (
                      <span className="bg-gray-200 text-gray-700 text-[10px] px-1.5 py-0.5 rounded font-medium">
                        {player.player_category}
                      </span>
                    )}
                    {player.batting_style && (
                      <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-medium">
                        {player.batting_style}
                      </span>
                    )}
                    {player.bowling_type && (
                      <span className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0.5 rounded font-medium">
                        {player.bowling_type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">₿ {formatBYC(player.sold_price_byc || 0)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
